import { motion } from "framer-motion";
import { ArrowUpRight, FlaskConical, Leaf, Atom } from "lucide-react";
import {
  MEDIA,
  SHELFANOX,
  SHELFANOX_PRODUCTS,
  NATPRO,
  SPECIALTY,
  SPECIALTY_PRODUCTS,
} from "../../data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Products = () => {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="scroll-mt-nav py-24 md:py-36 border-t border-ink/10 bg-bone"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="overline text-forest-accent mb-4">/ 02 — Products</div>
            <h2
              className="font-display font-bold leading-[1.02] tracking-tight text-ink text-balance"
              style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
            >
              Three product lines.
              <br />
              One chemistry discipline.
            </h2>
          </div>
          <div className="max-w-sm text-ink-soft leading-relaxed">
            <p>
              <span className="text-amber-accent font-medium">Shelfanox™</span>{" "}
              for shelf life.{" "}
              <span className="text-forest-accent font-medium">NatPro™</span>{" "}
              for naturally-derived nutrition. A{" "}
              <span className="text-steel-accent font-medium">
                Specialty Chemicals
              </span>{" "}
              range for polymer and pharmaceutical customers.
            </p>
          </div>
        </div>

        {/* ROW 1 — Antioxidants: Shelfanox + NatPro */}
        <div className="grid grid-cols-12 gap-5 md:gap-6">
          {/* Shelfanox — Synthetic Antioxidants */}
          <motion.article
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={0}
            data-testid="product-card-shelfanox"
            className="col-span-12 lg:col-span-7 relative overflow-hidden rounded-sm border border-ink/10 bg-ink text-bone group"
          >
            <div className="absolute inset-0">
              <img
                src={MEDIA.synthetic_amber}
                alt="Refracting laboratory glass in amber and navy"
                className="w-full h-full object-cover opacity-90 group-hover:scale-[1.03] transition-transform duration-[1500ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/65 to-ink/15" />
            </div>
            <div className="relative p-8 md:p-12 min-h-[460px] md:min-h-[560px] flex flex-col">
              <div className="flex items-center gap-2 mb-auto">
                <FlaskConical className="w-4 h-4 text-amber-accent" />
                <span className="overline text-amber-accent">
                  {SHELFANOX.tagline}
                </span>
              </div>
              <div>
                <h3
                  className="font-display font-black tracking-tight leading-[0.95]"
                  style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
                >
                  {SHELFANOX.brand}
                  <span className="text-amber-accent">™</span>
                </h3>
                <div className="mt-3 font-display text-bone/70 text-xl md:text-2xl tracking-tight leading-tight">
                  TBHQ · BHA · BHT · AP
                </div>
                <p className="mt-5 max-w-md text-bone/75 leading-relaxed">
                  {SHELFANOX.blurb}
                </p>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-bone/10">
                  {SHELFANOX_PRODUCTS.map((p) => (
                    <div
                      key={p.code}
                      data-testid={`shelfanox-${p.code.toLowerCase()}`}
                      className="bg-ink p-5 hover:bg-ink/70 transition-colors"
                    >
                      <div className="font-display font-bold tracking-tight text-amber-accent text-2xl leading-none">
                        {p.code}
                      </div>
                      <div className="mt-2 font-mono text-[11px] tracking-[0.1em] uppercase text-bone/55 whitespace-nowrap">
                        CAS {p.cas}
                      </div>
                    </div>
                  ))}
                </div>
                <a
                  href="#contact"
                  data-testid="shelfanox-spec-cta"
                  className="mt-8 inline-flex items-center gap-1.5 text-bone font-medium text-sm border-b border-amber-accent pb-0.5 hover:gap-2.5 transition-all"
                >
                  Request Shelfanox specifications
                  <ArrowUpRight className="w-3.5 h-3.5 text-amber-accent" />
                </a>
              </div>
            </div>
          </motion.article>

          {/* NatPro — Natural Antioxidants */}
          <motion.article
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={1}
            data-testid="product-card-natpro"
            className="col-span-12 lg:col-span-5 relative overflow-hidden rounded-sm border border-ink/10 bg-white group"
          >
            <div className="relative h-56 md:h-64 overflow-hidden">
              <img
                src={MEDIA.natural_green}
                alt="Golden-green botanical oil droplet"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1500ms]"
              />
              <div className="absolute top-4 left-4 bg-forest-accent text-bone px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] uppercase">
                Natural
              </div>
            </div>
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-2 mb-4">
                <Leaf className="w-4 h-4 text-forest-accent" />
                <span className="overline text-forest-accent">Natural Antioxidants</span>
              </div>
              <h3
                className="font-display font-black tracking-tight text-ink leading-[1] mb-1"
                style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
              >
                {NATPRO.brand}
                <span className="text-forest-accent">™</span>
              </h3>
              <div className="font-display text-ink/70 text-lg md:text-xl tracking-tight mb-4">
                Mixed Tocopherols
              </div>
              <p className="text-ink-soft leading-relaxed text-[15px]">
                {NATPRO.blurb}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {NATPRO.grades.map((g) => (
                  <span
                    key={g}
                    className="border border-forest-accent/30 text-forest-accent px-3 py-1 font-mono text-[11px] tracking-[0.15em] uppercase"
                  >
                    {g}
                  </span>
                ))}
              </div>
              <a
                href="#contact"
                data-testid="natpro-spec-cta"
                className="mt-8 inline-flex items-center gap-1.5 text-ink font-medium text-sm border-b border-ink pb-0.5 hover:gap-2.5 transition-all"
              >
                Request NatPro specifications
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.article>
        </div>

        {/* ROW 2 — Specialty Chemicals (separate full-width category) */}
        <motion.article
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          custom={2}
          data-testid="product-card-specialty"
          className="mt-5 md:mt-6 relative overflow-hidden rounded-sm border border-steel-accent/25 bg-white"
        >
          {/* Blueprint accent strip */}
          <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" aria-hidden />
          <div className="absolute top-0 left-0 right-0 h-1 bg-steel-accent" aria-hidden />

          <div className="relative grid grid-cols-12 gap-0">
            {/* Header column */}
            <div className="col-span-12 lg:col-span-4 p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-ink/10">
              <div className="flex items-center gap-2 mb-4">
                <Atom className="w-4 h-4 text-steel-accent" strokeWidth={1.5} />
                <span className="overline text-steel-accent">Specialty Chemicals</span>
              </div>
              <h3
                className="font-display font-black tracking-tight text-ink leading-[1] mb-4"
                style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
              >
                Specialty
                <br />
                phenolics.
              </h3>
              <p className="text-ink-soft leading-relaxed text-[15px] max-w-sm">
                {SPECIALTY.blurb}
              </p>
              <a
                href="#contact"
                data-testid="specialty-spec-cta"
                className="mt-7 inline-flex items-center gap-1.5 text-ink font-medium text-sm border-b border-steel-accent pb-0.5 hover:gap-2.5 transition-all"
              >
                Request Specialty specifications
                <ArrowUpRight className="w-3.5 h-3.5 text-steel-accent" />
              </a>
            </div>

            {/* Product columns */}
            {SPECIALTY_PRODUCTS.map((p, idx) => (
              <div
                key={p.code}
                data-testid={`specialty-${p.code.toLowerCase()}`}
                className={`col-span-12 sm:col-span-6 lg:col-span-4 p-8 md:p-10 ${
                  idx === 0 ? "border-b sm:border-b-0 sm:border-r border-ink/10" : ""
                }`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="font-display font-bold tracking-tight text-steel-accent text-2xl leading-none">
                      {p.code}
                    </div>
                    <div className="mt-2 font-mono text-[11px] tracking-[0.18em] uppercase text-ink-soft">
                      CAS {p.cas}
                    </div>
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-steel-accent border border-steel-accent/40 px-2 py-1">
                    {p.application}
                  </span>
                </div>
                <div className="font-display font-medium text-ink text-xl md:text-2xl leading-tight mb-5">
                  {p.name}
                </div>
                <ul className="space-y-2">
                  {p.uses.map((u) => (
                    <li
                      key={u}
                      className="text-sm text-ink-soft flex items-start gap-2"
                    >
                      <span className="w-1 h-1 bg-steel-accent rounded-full mt-2 shrink-0" />
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.article>

        {/* Detail strip — Shelfanox application matrix */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          custom={3}
          className="mt-14 md:mt-20 border-t border-ink/10 pt-10"
        >
          <div className="overline text-amber-accent mb-8">
            Shelfanox™ — application matrix
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {SHELFANOX_PRODUCTS.map((p) => (
              <div key={p.code} data-testid={`detail-${p.code.toLowerCase()}`}>
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-2">
                  {p.code}
                </div>
                <div className="font-display font-medium text-ink text-lg leading-tight mb-3">
                  {p.name}
                </div>
                <ul className="space-y-1.5">
                  {p.uses.map((u) => (
                    <li
                      key={u}
                      className="text-sm text-ink-soft flex items-start gap-2"
                    >
                      <span className="w-1 h-1 bg-amber-accent rounded-full mt-2 shrink-0" />
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
